import { createDialog, type DialogService } from '@patsnap/tahiti-core';
import { injectable } from '@patsnap/tahiti-core/shared/inversify';
import type {
    PricingDialogOptions,
    PricingDialogResult,
} from '@/presentation/components/pricing/PricingDialog.vue';

// Re-export types for external use
export type { PricingDialogOptions, PricingDialogResult };

/**
 * Pricing 对话框服务工厂
 * 基于 createDialog 创建对话框实例
 */
export const pricingDialogServiceFactory = createDialog(() => import('@/presentation/components/pricing/PricingDialog.vue'));

/**
 * Pricing 对话框服务接口
 */
export interface PricingDialogService {
    /**
     * 打开 Pricing 卡片对话框
     */
    openDialog(options?: PricingDialogOptions): Promise<PricingDialogResult>;
}

/**
 * Pricing 对话框服务实现
 */
@injectable()
export class PricingDialogServiceImpl implements PricingDialogService {
    private _dialogService = pricingDialogServiceFactory();

    /**
     * 打开 Pricing 卡片对话框
     * @param options 对话框配置选项
     * @returns Promise<PricingDialogResult> 对话框结果
     */
    async openDialog(options: PricingDialogOptions = {}): Promise<PricingDialogResult> {
        const dialog = this._dialogService;
        return new Promise((resolve, reject) => {
            // 设置对话框回调
            dialog.onSubmit(async (result, next) => {
                try {
                    // 处理提交逻辑
                    resolve(result);
                    next(); // 关闭对话框
                } catch (error) {
                    // 如果有错误，保持对话框打开
                    next(false);
                    reject(error);
                }
            });

            dialog.onCancel(() => {
                // 用户取消操作
                const result: PricingDialogResult = {
                    action: 'cancel',
                };
                resolve(result);
            });

            // 打开对话框
            dialog.open(options);
        });
    }

    /**
     * 打开简单的价格卡片对话框
     * @param title 对话框标题
     * @returns Promise<PricingDialogResult> 对话框结果
     */
    async openPricingCardsDialog(title?: string): Promise<PricingDialogResult> {
        return this.openDialog({
            title: title || 'Pricing Plans',
            width: '1200px',
            showCloseButton: true,
        });
    }
}

// 导出服务 Symbol
export const PricingDialogService = Symbol.for('PricingDialogService');
